import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import AreaGrupoForm from "../../components/modals/AreaGrupoForm";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const AreaGruposPage = () => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // 🔢 Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const API = process.env.REACT_APP_API_URL + "/area-grupos";
  const token = localStorage.getItem("token");

  const fetchGrupos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrupos(res.data);
      setCurrentPage(1); // reset al cargar
    } catch {
      toast.error("Error cargando grupos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrupos();
    // eslint-disable-next-line
  }, []);

  const handleSave = async (data) => {
    try {
      if (selected) {
        await axios.put(`${API}/${selected._id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Grupo actualizado");
      } else {
        await axios.post(API, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Grupo creado");
      }
      setShowModal(false);
      setSelected(null);
      fetchGrupos();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error guardando grupo");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este grupo?")) return;
    try {
      await axios.delete(`${API}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchGrupos();
    } catch {
      toast.error("No se pudo eliminar el grupo");
    }
  };

  // 📄 Lógica de paginación
  const totalPages = Math.ceil(grupos.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = grupos.slice(indexOfFirst, indexOfLast);

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Grupos de áreas</h2>
          {hasPermission("areas.crear") && (
            <Button onClick={() => setShowModal(true)}>+ Nuevo grupo</Button>
          )}
        </div>

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length ? (
                currentItems.map((g) => (
                  <tr key={g._id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4">{g.name}</td>
                    <td className="py-2 px-4 text-right">
                      {hasPermission("areas.editar") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelected(g);
                            setShowModal(true);
                          }}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission("areas.eliminar") && (
                        <Button
                          size="sm"
                          className="ml-2 bg-red-500 text-white"
                          onClick={() => handleDelete(g._id)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="text-center py-4 text-gray-500">
                    No hay grupos creados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔢 Controles de paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentItems.length} de {grupos.length} resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {showModal && (
          <AreaGrupoForm
            grupo={selected}
            onClose={() => {
              setShowModal(false);
              setSelected(null);
            }}
            onSave={handleSave}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AreaGruposPage;
